import { AnyAction, Dispatch } from 'redux'
import { getErrorMessages } from '../../../common/utils/error'
import { RequestFailureMessage } from '../../../common/utils/messagesContants'
import { updateAppAlert } from '../../../common/modules/appAlert/actions'
import {
  startLoading,
  stopLoading,
} from '../../../common/modules/loading/actions'
import { LoadingTypes } from '../../../common/modules/loading/reducer'
import { errorLogger } from '../../../common/utils/errorLogger'
import { AppDispatch, RootState } from '../../../store'
import { UsageDataState, UsageFilterState } from './reducer'
import { camelize } from 'casing'
import FileDownload from 'js-file-download'
import {
  fetchAzureMeteringReport,
  fetchMeteringReport,
  fetchUsageData,
  getSearchedData,
  fetchAzureUsageData,
} from '../../api/markeplace'
import { DateRange } from '../revenueInsightsV2/reducer'
import { isEmpty } from 'lodash'
import { AxiosError, AxiosResponse } from 'axios'
import { CloudMarketplace } from '../productsListing/reducer'

export enum UsageMeteringActionTypes {
  SET_USAGE_METERING_DATA = 'SET_USAGE_METERING_DATA',
  CLEAR_USAGE_METERING_DATA = 'CLEAR_USAGE_METERING_DATA',
  SET_USAGE_DATE_RANGE = 'SET_USAGE_DATE_RANGE',
  SET_USAGE_FILTERS = 'SET_USAGE_FILTERS',
  CLEAR_USAGE_FILTERS = 'CLEAR_USAGE_FILTERS',
  SET_PAGE_NUMBER = 'SET_PAGE_NUMBER',
}

export const getUsageData =
  ({
    pageSize,
    pageNumber,
    cloud,
    status,
    input,
    selectedInput,
    sectionName,
    accountId,
    cloudType,
  }: {
    pageSize?: number
    pageNumber?: number
    cloud?: string[]
    status?: string[]
    input?: string
    selectedInput?: string
    sectionName?: string
    accountId?: string
    cloudType: CloudMarketplace
  }) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.USAGE_LOADING))
    try {
      const partnerId =
        accountId || getState().AccountData.user.partnerData?.partnerId || ''
      const { dateRangeFilter, status } = getState().usageData.filters
      const finalStartDate = dateRangeFilter.startDate
      const finalEndDate = dateRangeFilter.endDate

      if (isEmpty(input)) {
        let data: any
        if (cloudType === 'AWS') {
          const response = await fetchUsageData({
            partnerId,
            pageNumber,
            pageSize,
            cloud,
            status,
            finalStartDate,
            finalEndDate,
          })
          data = response.data
        } else if (cloudType === 'AZURE') {
          const response = await fetchAzureUsageData({
            partnerId,
            pageNumber,
            pageSize,
            cloud,
            status,
            finalStartDate,
            finalEndDate,
          })
          data = response.data
        }
        await dispatch(setUsageMeteringData(camelize(data)))
      } else {
        let additionalParams = ''
        if (!isEmpty(status)) {
          additionalParams += `&status=${status?.join(',').toUpperCase()}`
        }
        if (!isEmpty(finalStartDate)) {
          additionalParams += `&from_date=${finalStartDate}`
        }
        if (!isEmpty(finalEndDate)) {
          additionalParams += `&to_date=${finalEndDate}`
        }

        const response = await getSearchedData({
          partnerId,
          input,
          selectedInput,
          pageSize,
          pageNumber,
          sectionName,
          additionalParams,
        })
        const numberOfPage = response?.data?.pagination_object?.current_page
        const totalCount = response?.data.total_count
        const rows = response?.data.records.map((recordItem: any) => ({
          ...recordItem._source,
          labraMetering_id: recordItem._source.id,
        }))
        const data = {
          ...response.data,
          data: rows,
          metaData: {
            page_size: pageSize,
            page_number: numberOfPage,
            sort_by: 'created_at',
            sort_order: 'desc',
            total_count: totalCount,
          },
        }
        await dispatch(setUsageMeteringData(camelize(data)))
      }
      await dispatch(
        updateAppAlert({
          message: '',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: any) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.USAGE_LOADING))
    }
  }
export const setUsageMeteringData = (usageData: UsageDataState) => {
  return {
    type: UsageMeteringActionTypes.SET_USAGE_METERING_DATA,
    payload: usageData,
  }
}
export const clearUsageMeteringData = () =>
  ({
    type: UsageMeteringActionTypes.CLEAR_USAGE_METERING_DATA,
  }) as unknown as AnyAction

export const getMeteringReportFile =
  (
    fromDate: string,
    toDate: string,
    cloudProductId: string,
    cloudProvider: string
  ) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.MARKETPLACE_INFO))

    try {
      const partnerId = getState().AccountData.user.partnerData?.partnerId || ''
      const response = await fetchMeteringReport({
        partnerId,
        fromDate,
        toDate,
        cloudProductId,
      })

      const filename = `metering_history_${cloudProvider}_${cloudProductId}_${fromDate}_${toDate}.csv`
      FileDownload(response.data, filename)

      dispatch(
        updateAppAlert({
          message: 'Metering history downloaded.',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      throw error
    } finally {
      await dispatch(stopLoading(LoadingTypes.MARKETPLACE_INFO))
    }
  }

export const getAzureMeteringReportFile =
  (fromDate: string, toDate: string, cloudProductCode: string) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.MARKETPLACE_INFO))

    try {
      const partnerId = getState().AccountData.user.partnerData?.partnerId || ''
      const response = await fetchAzureMeteringReport({
        partnerId,
        fromDate,
        toDate,
        cloudProductCode,
      })

      const filename = `metering_history_AZURE_${cloudProductCode}_${fromDate}_${toDate}.csv`
      FileDownload(response.data, filename)

      dispatch(
        updateAppAlert({
          message: 'Metering history downloaded.',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      throw error
    } finally {
      await dispatch(stopLoading(LoadingTypes.MARKETPLACE_INFO))
    }
  }
export const updateUsageDateRange =
  (dates: DateRange) => async (dispatch: AppDispatch) => {
    const { startDate, endDate } = dates
    await dispatch(setUsageDateRange({ startDate, endDate }))
  }

export const setUsageDateRange = (dates: DateRange) => ({
  type: UsageMeteringActionTypes.SET_USAGE_DATE_RANGE,
  payload: dates,
})
export const setFilters = (filters: UsageFilterState) => ({
  type: UsageMeteringActionTypes.SET_USAGE_FILTERS,
  payload: filters,
})
export const clearFilters = () => ({
  type: UsageMeteringActionTypes.CLEAR_USAGE_FILTERS,
})
export const setUsagePageNumber = (pageNumber: number) => ({
  type: UsageMeteringActionTypes.SET_PAGE_NUMBER,
  payload: pageNumber,
})
